import KCustomerAddressCard from "kinnect-front/KCustomers/KCustomerAddressCard";
import KWeatherCell from "kinnect-front/KYard/Schedule/KWeatherCell";
import { Crudable } from "kinnect-mixins";
import {
	KActionDialog,
	KDataTable,
	KDataTableMenu,
	KEditDialog,
	KMapLink,
	KTextField,
	KTimeAgo,
	KValidatableInput
} from "kinnect-general";
import {
	VIcon,
	VList,
	VListItem,
	VListItemAction,
	VListItemContent,
	VListItemTitle
} from "vuetify/lib";
import { formatDate, formatTime } from "kinnect-formatters";

export default KDataTable.extend( {
	name: "KSchedulesTable",

	mixins: [
		Crudable,
		KValidatableInput
	],

	props: {
		editLoadTime: {
			default: true,
			type: Boolean
		},

		linkAddress: {
			default: true,
			type: Boolean
		},

		noDataText: {
			...KDataTable.options.props.noDataText,
			default: "There are no schedules to display"
		},

		noResultsText: {
			...KDataTable.options.props.noResultsText,
			default: "No matching schedules found"
		},

		sortBy: {
			...KDataTable.options.props.sortBy,
			default: "deliveryDate"
		},

		sortDesc: {
			...KDataTable.options.props.sortDesc,
			default: true
		},

		value: {
			type: Array,
			default: () => []
		},

		weather: Boolean
	},

	data: () => ( {
		crud: "yard_schedules"
	} ),

	computed: {
		showWeatherCol() {
			return this.weather
				&& this.items
				&& this.items.some( schedule => Boolean( schedule.weather ) );
		},

		showAgeCol() {
			return this.canAdd || this.canEdit;
		},

		defaultHeaders() {
			return [
				{
					text: "Date",
					value: "deliveryDate"
				},
				{
					text: "Time",
					value: "deliveryTime"
				},
				{
					text: "Type(s)",
					value: "scheduleTypeNames"
				},
				this.showAgeCol && {
					text: "Age",
					value: "age"
				},
				{
					text: "Address",
					value: "address.fullAddress"
				},
				this.showWeatherCol && {
					sortable: false,
					text: "Weather",
					value: "weather"
				},
				{
					text: "Salesman",
					value: "salesman.user.name"
				},
				{
					text: "Customer",
					value: "customer.name"
				},
				{
					text: "SO#",
					value: "salesOrderNumbers"
				},
				{
					text: "PO#",
					value: "purchaseOrderNumbers"
				},
				{
					text: "Desc",
					value: "description",
					width: "150px"
				},
				{
					class: { "text-no-wrap": true },
					text: "Load Time",
					value: "estLoadTime"
				},
				{
					align: "end",
					sortable: false,
					text: this.isMobile ? "Actions" : "",
					value: "actions"
				}
			];
		}
	},

	methods: {
		approveSchedule( schedule ) {
			schedule.approved = true;

			return schedule.save().then( () => {
				this.removeScheduleData( schedule );
			} );
		},

		saveLoadTime( schedule, estLoadTime ) {
			schedule.estLoadTime = estLoadTime;

			return schedule.save();
		},

		deleteSchedule( schedule ) {
			return schedule.delete().then( () => {
				this.removeScheduleData( schedule );
			} );
		},

		removeScheduleData( schedule ) {
			const deleteIndex = this.items.findIndex( item => schedule.id === item.id );

			if ( deleteIndex !== -1 ) {
				this.$delete( this.items, deleteIndex );
			}

			this.$emit( "scheduleRemoved", schedule );
		},

		genValidatableField( props ) {
			return <KDataTable
				attrs={this.$props}
				hasErrors={Boolean( props.errors.length )}
				headers={this.$_headers}
				on={{ ...this.$listeners }}
				scopedSlots={{
					"header.data-table-select": () => "Select",

					"item.deliveryDate": ( { item } ) =>
						<span class="text-no-wrap">
							{item.deliveryDate
								? formatDate( item.deliveryDate, "MMM d" )
								: "--"}
						</span>,

					"item.deliveryTime": ( { item } ) =>
						<span class="text-no-wrap">
							{item.deliveryTime
								? formatTime( item.deliveryTime )
								: "--"}
						</span>,

					"item.age": ( { item } ) =>
						<KTimeAgo
							includeSeconds={false}
							startTime={item.createdTimestamp}
						></KTimeAgo>,

					"item.address.fullAddress": ( { item } ) => (
						this.linkAddress
							? <KMapLink
								address={item.address.address}
								city={item.address.city}
								format="a, c"
								latitude={item.address.lat}
								longitude={item.address.lng}
								state={item.address.state}
								zip={item.address.zip}
							></KMapLink>
							: item.address.fullAddress
					),

					"item.weather": ( { item } ) =>
						<KWeatherCell schedule={item}></KWeatherCell>,

					"item.customer.name": ( { item } ) => (
						item.customer
							? <KCustomerAddressCard
								address={item.address}
								customer={item.customer}
							></KCustomerAddressCard>
							: "--"
					),

					"item.estLoadTime": ( { item } ) => (
						this.editLoadTime && this.canEdit
							? <KEditDialog
								on={{ "update:return-value": val => item.estLoadTime = val }}
								onSave={loadTime => this.saveLoadTime( item, loadTime )}
								returnValue={item.estLoadTime}
								title="Update Load Time"
								value={item.estLoadTime}
								scopedSlots={{
									activator: ( { on } ) =>
										<div
											class="pa-1 text-no-wrap d-inline-block"
											on={on}
											vRipple
										>
											<VIcon small>edit</VIcon> {parseFloat( item.estLoadTime || 0 )} min.
										</div>
								}}
							>
								<KTextField
									min="0"
									singleLine
									slot="input"
									step="5"
									suffix="minutes"
									type="number"
									vModel={item.estLoadTime}
								></KTextField>
							</KEditDialog>
							: `${parseFloat( item.estLoadTime || 0 )} min.`
					),

					"item.actions": ( { item } ) =>
						<KDataTableMenu>
							<VList>
								{!item.$.approved && this.canEdit && [
									<KActionDialog
										confirmButtonColor="success"
										confirmButtonText="Approve"
										loading={this.$wait.is( "save schedule" )}
										onConfirm={() => this.approveSchedule( item )}
										scopedSlots={{
											activator: ( { on } ) =>
												<VListItem
													color="success"
													on={on}
												>
													<VListItemAction>
														<VIcon color="success">thumb_up</VIcon>
													</VListItemAction>

													<VListItemContent>
														<VListItemTitle>Approve</VListItemTitle>
													</VListItemContent>
												</VListItem>
										}}
										title="Approve Schedule Request"
									>
										Are you sure you want to approve the <strong>{item.scheduleTypeNames}</strong> schedule for <strong>{item.address.address}</strong>?
									</KActionDialog>,

									<KActionDialog
										confirmButtonColor="error"
										confirmButtonText="Reject and Delete"
										loading={this.$wait.is( "delete schedule" )}
										onConfirm={() => this.deleteSchedule( item )}
										scopedSlots={{
											activator: ( { on } ) =>
												<VListItem
													color="error"
													on={on}
												>
													<VListItemAction>
														<VIcon color="error">thumb_down</VIcon>
													</VListItemAction>

													<VListItemContent>
														<VListItemTitle>Reject</VListItemTitle>
													</VListItemContent>
												</VListItem>
										}}
										title="Reject Schedule Request"
									>
										Are you sure you want to reject the <strong>{item.scheduleTypeNames}</strong> schedule for <strong>{item.address.address}</strong>? This will delete the requested schedule.
									</KActionDialog>
								]}

								<VListItem to={{
									name: "yardSchedule",
									params: { idSchedule: item.id }
								}}>
									<VListItemAction>
										<VIcon>visibility</VIcon>
									</VListItemAction>

									<VListItemContent>
										<VListItemTitle>View</VListItemTitle>
									</VListItemContent>
								</VListItem>

								{this.canDelete && <KActionDialog
									confirmButtonColor="error"
									confirmButtonText="Delete"
									loading={this.$wait.is( "delete schedule" )}
									onConfirm={() => this.deleteSchedule( item )}
									scopedSlots={{
										activator: ( { on } ) =>
											<VListItem on={on}>
												<VListItemAction>
													<VIcon>delete</VIcon>
												</VListItemAction>

												<VListItemContent>
													<VListItemTitle>Delete</VListItemTitle>
												</VListItemContent>
											</VListItem>
									}}
									title="Delete Schedule"
								>
									Are you sure you want to delete the <strong>{item.scheduleTypeNames}</strong> schedule for <strong>{item.address.address}</strong>?
								</KActionDialog>}
							</VList>
						</KDataTableMenu>,

					...this.$scopedSlots
				}}
				vModel={this.internalValue}
			></KDataTable>;
		}
	},

	render() {
		if ( this.showSelect ) {
			this.genValidationProvider();
		}

		return this.genValidatableField( { errors: [] } );
	}
} );
